import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getMarketingConsent, isMarketingPath, onMarketingConsentChange } from '../../lib/consent';
import { trackMetaEvent } from '../../lib/metaPixel';

/**
 * Meta Pixel (campañas Meta Ads) — solo en rutas públicas y solo con consentimiento.
 *
 * Por qué no el snippet tal cual en public/index.html:
 * - El Pixel envía la URL completa (query + hash) a Meta. /activar-cuenta lleva
 *   el token de activación del paciente, /pagar/:consultaId el UUID de la
 *   consulta y /panel es el portal clínico → nunca deben llegar a Meta.
 * - fbevents.js dispara PageView solo en cada pushState del SPA; con
 *   disablePushState lo apagamos y disparamos PageView a mano solo en la allowlist.
 * - autoConfig=false apaga los "automatic events" (lectura de botones/formularios),
 *   para no mandar datos de pacientes (salud = dato sensible, Ley 19.628/21.719).
 *   Automatic Advanced Matching se apaga aparte en Events Manager.
 * - Opt-in: el script no se descarga hasta que la persona acepta en el
 *   CookieConsentBanner. Si después rechaza, se revoca y se borran sus cookies.
 */

const PIXEL_ID = '712497088323067';

type Fbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[];
  push: Fbq;
  loaded: boolean;
  version: string;
  disablePushState?: boolean;
};

declare global {
  interface Window {
    fbq?: Fbq;
    _fbq?: Fbq;
  }
}

const isProductionHost = () => window.location.hostname.endsWith('conectemoschile.cl');

function loadPixel() {
  if (window.fbq) return;

  const fbq = function (...args: unknown[]) {
    if (fbq.callMethod) fbq.callMethod.apply(fbq, args);
    else fbq.queue.push(args);
  } as Fbq;
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = '2.0';
  fbq.queue = [];
  fbq.disablePushState = true;
  window.fbq = fbq;
  if (!window._fbq) window._fbq = fbq;

  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(script);

  fbq('set', 'autoConfig', false, PIXEL_ID);
  fbq('init', PIXEL_ID);
}

function clearMetaCookies() {
  const domains = ['', `; domain=${window.location.hostname}`, '; domain=.conectemoschile.cl'];
  ['_fbp', '_fbc'].forEach((name) =>
    domains.forEach((d) => {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${d}`;
    })
  );
}

export default function MetaPixel() {
  const { pathname } = useLocation();
  const [consent, setConsent] = useState(getMarketingConsent);

  useEffect(
    () =>
      onMarketingConsentChange((value) => {
        setConsent(value);
        if (value === 'denied' && window.fbq) {
          window.fbq('consent', 'revoke');
          clearMetaCookies();
        } else if (value === 'granted' && window.fbq) {
          window.fbq('consent', 'grant');
        }
      }),
    []
  );

  useEffect(() => {
    if (consent !== 'granted' || !isProductionHost() || !isMarketingPath(pathname)) return;
    loadPixel();
    window.fbq?.('track', 'PageView');
  }, [pathname, consent]);

  // Contact: un solo listener para todos los links de WhatsApp / teléfono / correo
  // del sitio público, en vez de instrumentar cada botón por separado.
  useEffect(() => {
    if (consent !== 'granted') return;
    const onClick = (e: MouseEvent) => {
      const link = (e.target as Element | null)?.closest?.('a[href]');
      const href = link?.getAttribute('href') ?? '';
      if (/wa\.me\/|api\.whatsapp\.com|^tel:|^mailto:/i.test(href)) trackMetaEvent('Contact');
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [consent]);

  return null;
}
